/**
 * PlaceDetailSheet — fullscreen place detail for the Welcome Book Explore tab.
 *
 * Layout (mobile-first, consistent with the rest of the Welcome Book):
 *   ┌──────────────────────────┐
 *   │  Hero image (h-56)   [X] │
 *   ├──────────────────────────┤
 *   │  Info card               │
 *   │  · Name + category badge │
 *   │  · Distance / rating chips│
 *   │  · Description           │
 *   │  · Address • Phone • Web │
 *   │  · (optional discount)   │
 *   ├──────────────────────────┤
 *   │  Full-bleed OSM map      │  ← 320px, place pin + blue user pin
 *   │   Click pin → open Maps  │
 *   ├──────────────────────────┤
 *   │  "Apri in Google Maps"   │
 *   └──────────────────────────┘
 *
 * Geolocation is requested on mount (guest is asked once by the browser).
 * If denied or unavailable the blue "you are here" pin is simply hidden;
 * everything else keeps working.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  X, MapPin, Phone, Star, Navigation, Globe, Clock, Ruler, LocateFixed, Loader2,
} from "lucide-react";
import LazyImage from "./LazyImage";

/* ── Marker icons ─────────────────────────────────────────────────────────── */
// Emerald "place" pin — matches the Welcome Book Explore accent
const placeIcon = L.divIcon({
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="52" viewBox="0 0 40 52">
    <defs>
      <filter id="pl-shadow" x="-20%" y="-10%" width="140%" height="130%">
        <feDropShadow dx="0" dy="3" stdDeviation="2.5" flood-color="#000" flood-opacity="0.30"/>
      </filter>
    </defs>
    <path filter="url(#pl-shadow)" d="M20 0C9 0 0 8.5 0 19.5 0 34 20 52 20 52s20-18 20-32.5C40 8.5 31 0 20 0z" fill="#10B981"/>
    <circle cx="20" cy="19.5" r="10" fill="#fff"/>
    <circle cx="20" cy="19.5" r="5" fill="#10B981"/>
  </svg>`,
  className: "place-marker",
  iconSize: [40, 52],
  iconAnchor: [20, 52],
  popupAnchor: [0, -46],
});

// Pulsing blue "you are here" pin — same style used across the app
const gpsIcon = L.divIcon({
  html: `<div style="position:relative;width:22px;height:22px;">
    <div style="position:absolute;inset:-12px;background:#3b82f6;opacity:0.22;border-radius:50%;animation:pdsPulse 2s infinite;"></div>
    <div style="width:22px;height:22px;background:#3b82f6;border:4px solid #fff;border-radius:50%;box-shadow:0 0 0 2px #3b82f6,0 4px 12px rgba(59,130,246,0.55);"></div>
  </div>
  <style>@keyframes pdsPulse{0%{transform:scale(0.6);opacity:0.55}70%{transform:scale(1.6);opacity:0}100%{opacity:0}}</style>`,
  className: "gps-marker",
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

/* ── Map controller: zoom-to-fit su richiesta geolocalizzazione ─────────────── */
function MapController({ placeCoords, gps, fitToken }) {
  const map = useMap();
  useEffect(() => {
    if (!fitToken) return;
    if (placeCoords && gps) {
      const b = L.latLngBounds([[placeCoords.lat, placeCoords.lng], [gps.lat, gps.lng]]);
      map.fitBounds(b, { padding: [70, 70], maxZoom: 16, animate: true });
    } else if (gps) {
      map.flyTo([gps.lat, gps.lng], 16, { animate: true });
    } else if (placeCoords) {
      map.flyTo([placeCoords.lat, placeCoords.lng], 16, { animate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitToken]);
  return null;
}

/* ── Translations ─────────────────────────────────────────────────────────── */
const T = {
  it: { close: "Chiudi", openMaps: "Apri in Google Maps", navigate: "Naviga", call: "Chiama", website: "Sito web", recommended: "Consigliato dal tuo host", discount: "sconto riservato", discountHow: "Mostra il messaggio", guestOf: "Ospite di", walking: "a piedi", from: "dalla struttura", locate: "Dove sono", attribution: "© OpenStreetMap contributors" },
  en: { close: "Close", openMaps: "Open in Google Maps", navigate: "Directions", call: "Call", website: "Website", recommended: "Recommended by your host", discount: "reserved discount", discountHow: "Show this message", guestOf: "Guest of", walking: "walk", from: "from the property", locate: "Locate me", attribution: "© OpenStreetMap contributors" },
  fr: { close: "Fermer", openMaps: "Ouvrir dans Google Maps", navigate: "Itinéraire", call: "Appeler", website: "Site web", recommended: "Recommandé par votre hôte", discount: "remise réservée", discountHow: "Montrez ce message", guestOf: "Invité de", walking: "à pied", from: "du logement", locate: "Me localiser", attribution: "© OpenStreetMap contributors" },
  es: { close: "Cerrar", openMaps: "Abrir en Google Maps", navigate: "Ir", call: "Llamar", website: "Sitio web", recommended: "Recomendado por tu anfitrión", discount: "descuento reservado", discountHow: "Muestra este mensaje", guestOf: "Huésped de", walking: "a pie", from: "desde el alojamiento", locate: "Ubicarme", attribution: "© OpenStreetMap contributors" },
  de: { close: "Schließen", openMaps: "In Google Maps öffnen", navigate: "Route", call: "Anrufen", website: "Website", recommended: "Vom Gastgeber empfohlen", discount: "reservierter Rabatt", discountHow: "Zeigen Sie diese Nachricht", guestOf: "Gast von", walking: "zu Fuß", from: "von der Unterkunft", locate: "Mein Standort", attribution: "© OpenStreetMap contributors" },
};

/* ── Helpers ──────────────────────────────────────────────────────────────── */
const gMapsDir = (lat, lng) => {
  // Google Directions API vuole SOLO "lat,lng" nel destination (niente nome,
  // altrimenti interpreta tutta la stringa come query e fallisce la navigazione).
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
};

// Try to extract lat/lng from a Google Maps URL. Handles the common patterns
// hosts paste in the editor (`/maps/place/.../@lat,lng`, `?q=lat,lng`,
// `?ll=lat,lng`, `?daddr=lat,lng`). Short URLs (goo.gl / maps.app.goo.gl)
// cannot be resolved client-side and return null.
const parseCoordsFromMapsUrl = (url) => {
  if (!url || typeof url !== "string") return null;
  const patterns = [
    /@(-?\d+\.\d+),(-?\d+\.\d+)/,
    /[?&](?:q|ll|daddr|destination|center)=(-?\d+\.\d+),(-?\d+\.\d+)/,
    /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) {
      const lat = parseFloat(m[1]);
      const lng = parseFloat(m[2]);
      if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    }
  }
  return null;
};

// Nominatim geocode with 30d localStorage cache. Nominatim rate-limits to
// ~1 req/sec/IP — we cache aggressively so the same address is queried once.
const geocodeCached = async (query) => {
  if (!query || query.trim().length < 3) return null;
  const key = `geo::${query.toLowerCase().trim()}`;
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const cached = JSON.parse(raw);
      if (cached && cached.expiry > Date.now()) return cached.coords || null;
    }
  } catch { /* noop */ }
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=1&addressdetails=0&q=${encodeURIComponent(query)}`,
      { headers: { Accept: "application/json" } },
    );
    if (!res.ok) return null;
    const arr = await res.json();
    const first = arr?.[0];
    const coords = first ? { lat: parseFloat(first.lat), lng: parseFloat(first.lon) } : null;
    try {
      localStorage.setItem(key, JSON.stringify({
        coords,
        expiry: Date.now() + 30 * 24 * 60 * 60 * 1000, // 30 days
      }));
    } catch { /* noop */ }
    return coords;
  } catch {
    return null;
  }
};

export default function PlaceDetailSheet({ place, propertyName, propertyLat, propertyLng, propertyCity, lang = "it", onClose }) {
  const t = T[lang] || T.it;
  const [gps, setGps] = useState(null);
  const [locating, setLocating] = useState(false);
  const [fitToken, setFitToken] = useState(0);
  // Coordinate del locale (pin verde). null finché non risolte.
  const [resolvedCoords, setResolvedCoords] = useState(() => {
    if (typeof place?.lat === "number" && typeof place?.lng === "number") {
      return { lat: place.lat, lng: place.lng };
    }
    return null;
  });
  // Fallback per centrare la mappa quando il locale non ha coordinate.
  const [cityCoords, setCityCoords] = useState(null);

  useEffect(() => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setGps(null),
      { enableHighAccuracy: false, maximumAge: 60000, timeout: 8000 },
    );
    return () => { try { navigator.geolocation.clearWatch(id); } catch { /* noop */ } };
  }, []);

  // Second-chance coord resolution — parse maps_url, then geocode.
  useEffect(() => {
    if (resolvedCoords) return;
    // 1) Parse maps_url
    const fromUrl = parseCoordsFromMapsUrl(place?.maps_url);
    if (fromUrl) { setResolvedCoords(fromUrl); return; }
    // 2) Geocode via Nominatim
    const cityLabel = typeof propertyCity === "string"
      ? propertyCity
      : (propertyCity?.nome || "");
    const parts = [place?.name, place?.address, cityLabel].filter(Boolean);
    if (parts.length === 0) return;
    const query = parts.join(", ");
    let cancelled = false;
    (async () => {
      const coords = await geocodeCached(query);
      if (!cancelled && coords) setResolvedCoords(coords);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [place?.maps_url, place?.name, place?.address, propertyCity]);

  const hasCoords = !!resolvedCoords;

  // Coordinate della struttura (host) come fallback per centrare la mappa.
  const propertyCoords = useMemo(() => {
    const la = typeof propertyLat === "number" ? propertyLat : parseFloat(propertyLat);
    const ln = typeof propertyLng === "number" ? propertyLng : parseFloat(propertyLng);
    return Number.isFinite(la) && Number.isFinite(ln) ? { lat: la, lng: ln } : null;
  }, [propertyLat, propertyLng]);

  // Se il locale non ha coordinate e nemmeno la struttura, geocodifica la città
  // così la mappa può comunque essere mostrata (mai schermo vuoto).
  useEffect(() => {
    if (resolvedCoords || propertyCoords || cityCoords) return;
    const cityLabel = typeof propertyCity === "string" ? propertyCity : (propertyCity?.nome || "");
    if (!cityLabel) return;
    let cancelled = false;
    (async () => {
      const coords = await geocodeCached(cityLabel);
      if (!cancelled && coords) setCityCoords(coords);
    })();
    return () => { cancelled = true; };
  }, [resolvedCoords, propertyCoords, cityCoords, propertyCity]);

  // Centro della mappa: locale → struttura → città. La mappa esce SEMPRE.
  const mapCenter = resolvedCoords || propertyCoords || cityCoords;

  // Richiesta geolocalizzazione on-demand + zoom per inquadrare locale e posizione.
  const locateMe = useCallback(() => {
    if (!navigator.geolocation) return;
    if (gps) { setFitToken((n) => n + 1); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocating(false); setFitToken((n) => n + 1); },
      () => { setLocating(false); },
      { enableHighAccuracy: true, maximumAge: 30000, timeout: 10000 },
    );
  }, [gps]);

  const mapsUrl = useMemo(() => {
    if (hasCoords) return gMapsDir(resolvedCoords.lat, resolvedCoords.lng);
    // Ultimate fallback: if the host provided a manual maps_url, respect it.
    if (place?.maps_url) return place.maps_url;
    // Otherwise, search by name + city.
    const cityLabel = typeof propertyCity === "string" ? propertyCity : (propertyCity?.nome || "");
    const q = [place?.name, place?.address, cityLabel].filter(Boolean).join(", ");
    return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : null;
  }, [hasCoords, resolvedCoords, place?.name, place?.maps_url, place?.address, propertyCity]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[5000] bg-zinc-50 overflow-y-auto"
      data-testid="place-detail-sheet"
    >
      {/* ── Hero ───────────────────────────────────────────────────────── */}
      {place.image_url ? (
        <div className="relative w-full h-56 sm:h-72">
          <LazyImage
            src={place.image_url.startsWith("http") ? place.image_url : `/api/files/${place.image_url}`}
            alt={place.name}
            className="w-full h-full"
            width={900}
            eager
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-black/10 to-transparent pointer-events-none" />
          <button
            onClick={onClose}
            data-testid="place-detail-close"
            className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/95 backdrop-blur-md shadow-lg flex items-center justify-center hover:scale-105 transition"
            aria-label={t.close}
          >
            <X className="w-5 h-5 text-zinc-800" strokeWidth={2.5} />
          </button>
        </div>
      ) : (
        <div className="relative w-full h-32 bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center">
          <MapPin className="w-10 h-10 text-emerald-300" />
          <button
            onClick={onClose}
            data-testid="place-detail-close"
            className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/95 backdrop-blur-md shadow-lg flex items-center justify-center"
            aria-label={t.close}
          >
            <X className="w-5 h-5 text-zinc-800" strokeWidth={2.5} />
          </button>
        </div>
      )}

      {/* ── Info card (overlaps hero by −24px) ─────────────────────────── */}
      <div className="px-4 sm:px-6 -mt-6 relative z-10">
        <motion.div
          initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          className="bg-white rounded-3xl p-5 shadow-sm border border-zinc-100"
        >
          {place.host_recommended && (
            <div className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full mb-2.5" data-testid="place-detail-recommended-badge">
              <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" strokeWidth={0} /> {t.recommended}
            </div>
          )}
          <h2 className="text-2xl font-black text-zinc-900 tracking-tight leading-tight">{place.name}</h2>
          <div className="flex items-center gap-2 flex-wrap mt-2">
            {place.category && (
              <span className="inline-block text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full">
                {place.category}
              </span>
            )}
            {place.rating != null && (
              <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-100 px-2.5 py-1 rounded-full">
                <Star className="w-3 h-3 fill-amber-400 text-amber-400" strokeWidth={0} /> {Number(place.rating).toFixed(1)}
              </span>
            )}
            {(place.distance_min ?? place.walking_time) && (
              <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-sky-700 bg-sky-50 border border-sky-100 px-2.5 py-1 rounded-full">
                <Clock className="w-3 h-3" strokeWidth={2.4} /> {place.walking_time || `${place.distance_min} min ${t.walking}`}
              </span>
            )}
          </div>

          {(place.description || place.why_go) && (
            <p className="text-[14px] text-zinc-600 mt-4 leading-relaxed whitespace-pre-line">
              {place.description || place.why_go}
            </p>
          )}

          {place.address && (
            <div className="flex items-start gap-2 mt-4 text-[13px] text-zinc-600">
              <MapPin className="w-4 h-4 text-zinc-400 shrink-0 mt-0.5" strokeWidth={2} />
              <span className="leading-snug">{place.address}</span>
            </div>
          )}

          {/* Quick contacts row */}
          {(place.phone || place.website) && (
            <div className="flex gap-2 mt-4">
              {place.phone && (
                <a
                  href={`tel:${place.phone}`}
                  className="flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 bg-sky-500 hover:bg-sky-600 text-white rounded-2xl py-3 text-[13px] font-black transition"
                  data-testid="place-detail-call"
                >
                  <Phone className="w-4 h-4" strokeWidth={2.4} /> {t.call}
                </a>
              )}
              {place.website && (
                <a
                  href={place.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 text-white rounded-2xl py-3 text-[13px] font-black transition"
                  data-testid="place-detail-website"
                >
                  <Globe className="w-4 h-4" strokeWidth={2.4} /> {t.website}
                </a>
              )}
            </div>
          )}

          {place.discount_percent > 0 && (
            <div className="mt-4 bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-2xl px-4 py-3">
              <p className="text-sm font-black text-emerald-800">−{place.discount_percent}% {t.discount}</p>
              <p className="text-xs text-emerald-700 mt-1 leading-relaxed">
                {t.discountHow}: "<span className="font-black">{t.guestOf} {propertyName}</span>"
              </p>
            </div>
          )}
        </motion.div>
      </div>

      {/* ── Map (full-bleed) — mostrata SEMPRE che ci sia un centro valido ── */}
      {mapCenter && (
        <div className="mt-5" data-testid="place-detail-map">
          <div className="relative w-full h-[360px] sm:h-[420px] overflow-hidden">
            <MapContainer
              center={[mapCenter.lat, mapCenter.lng]}
              zoom={hasCoords ? 16 : 14}
              scrollWheelZoom={false}
              zoomControl={false}
              className="w-full h-full"
              style={{ background: "#f4f4f5" }}
              attributionControl={false}
            >
              <TileLayer
                attribution={t.attribution}
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {hasCoords && (
                <Marker
                  position={[resolvedCoords.lat, resolvedCoords.lng]}
                  icon={placeIcon}
                  eventHandlers={{
                    click: () => { if (mapsUrl) window.open(mapsUrl, "_blank", "noopener"); },
                  }}
                >
                  <Popup closeButton={false} className="place-popup">
                    <div className="text-center">
                      <p className="text-[11px] font-black text-zinc-900 leading-tight">{place.name}</p>
                      <a
                        href={mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-[11px] font-black text-emerald-600"
                      >
                        {t.navigate} <Navigation className="w-3 h-3" />
                      </a>
                    </div>
                  </Popup>
                </Marker>
              )}
              {gps && (
                <Marker position={[gps.lat, gps.lng]} icon={gpsIcon} interactive={false} />
              )}
              <MapController placeCoords={resolvedCoords} gps={gps} fitToken={fitToken} />
            </MapContainer>

            {/* Bottone "Dove sono" — richiede la posizione e zooma per inquadrare
                sia il pin del locale sia il pin pulsante dell'utente. */}
            <button
              onClick={locateMe}
              disabled={locating}
              data-testid="place-detail-locate"
              className="absolute top-3 right-3 z-[1000] w-11 h-11 rounded-full bg-white shadow-lg flex items-center justify-center text-sky-600 hover:scale-105 active:scale-95 transition disabled:opacity-60"
              aria-label={t.locate}
              title={t.locate}
            >
              {locating ? <Loader2 className="w-5 h-5 animate-spin" /> : <LocateFixed className="w-5 h-5" strokeWidth={2.4} />}
            </button>

            {/* Attribution pill — leaflet's default is disabled, we render our own */}
            <div className="absolute bottom-2 right-2 text-[9px] font-medium text-zinc-600 bg-white/85 backdrop-blur-sm px-2 py-0.5 rounded-full pointer-events-none">
              {t.attribution}
            </div>
          </div>
        </div>
      )}

      {/* ── CTA "Apri in Google Maps" — SEMPRE presente (l'host inserisce il link) ── */}
      {mapsUrl && (
        <div className="px-4 sm:px-6 pb-8 pt-4 bg-zinc-50">
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="place-detail-open-maps"
            className="flex items-center justify-center gap-2 bg-zinc-900 hover:bg-black text-white rounded-2xl py-4 font-black text-sm shadow-lg shadow-zinc-900/20 transition"
          >
            <Navigation className="w-4 h-4" strokeWidth={2.5} /> {t.openMaps}
          </a>
        </div>
      )}

      {/* Bottom padding quando non c'è né mappa né CTA */}
      {!mapCenter && !mapsUrl && <div className="h-10" />}
    </motion.div>
  );
}
